import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import type { ShopifySyncProgress } from "@/types/integrations"

/**
 * OKS-960 — The running-import status line, in its TWO renderings.
 *
 * ## Live vs snapshot is the whole point of this component (PRD AC19 / Core Rule 11)
 *
 * `live` means signals are actually arriving for THIS viewer: they started the
 * import (`started_by_viewer`) **and** the socket is connected. Anything else —
 * an import a colleague or the weekly job started, or a blocked socket — is the
 * snapshot rendering.
 *
 * ⚠️ **Where nothing is arriving, nothing may move.** The moving parts are what
 * claim "this is live", so the snapshot rendering has NO spinner and no
 * `animate-*` anything. A spinner that never stops beside a number that never
 * moves is a worse lie than showing no liveness at all.
 *
 * ⚠️ **The absent spinner is not the signal.** An absence decays silently and
 * nobody notices it, so the COPY carries it (`sync.progress.snapshot`) and a
 * look-again control lets the brand move the numbers themselves. That control
 * is a one-shot reload on click — not polling, not a timer — and it renders for
 * anyone who can see the page, because it is a read.
 *
 * ## ⚠️ Only the NUMBERS are `aria-hidden`, never the disclosure or the control
 *
 * The visible ticker is hidden from assistive tech because a per-page-updating
 * region would interrupt a screen reader up to 20 times per import; milestones
 * are announced by `SyncAnnouncer` instead. But `aria-hidden` on an ancestor
 * removes everything inside it from the accessibility tree — so the snapshot
 * disclosure (which IS the AC19 signal) and the look-again button (which must
 * be operable) live OUTSIDE that wrapper. Putting them inside would make the
 * one state that exists to be honest about liveness invisible to the users most
 * dependent on being told.
 */
export default function SyncProgressTicker({
  progress,
  pending,
  live,
  onRefresh,
  refreshing,
}: {
  /** `null` while an optimistic submit is in flight and no server progress has
   *  arrived yet. */
  progress: ShopifySyncProgress | null
  /** The Inertia form's `processing` — see the retraction note in `SyncPanel`. */
  pending: boolean
  live: boolean
  onRefresh: () => void
  refreshing: boolean
}) {
  const { t } = useTranslation()

  // `page === 0` is the legitimate pre-first-page state, not "no progress".
  //
  // ⚠️ `!= null` (LOOSE), never `!== null`. Strict-null lets `undefined`
  // through — and `undefined !== null` is TRUE, so the next dereference throws
  // and React unmounts the whole tree. That is not hypothetical: this
  // component blanked `/brand/settings/products` for every connected brand
  // when the server briefly emitted `sync_progress` top-level instead of on
  // the store. The serializer is fixed, but a component should degrade when a
  // prop is missing rather than depend on the server never omitting one.
  //
  // Loose `!= null` rather than optional chaining because it does both jobs:
  // it excludes `undefined` AND narrows `progress` for the branch below, which
  // `(progress?.page ?? 0) > 0` does not.
  const landed = progress != null && progress.page > 0
  const showSnapshotNotice = !live && !pending

  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span
        className="flex flex-wrap items-center gap-x-2"
        data-testid="brand-products-sync-progress"
        aria-hidden="true"
      >
        {live ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}

        <span>{t("settings.brand.products.sync.progress.heading")}</span>

        {landed ? (
          <>
            <span>·</span>
            <span className="tabular-nums">
              {t("settings.brand.products.sync.progress.page", {
                page: progress.page,
              })}
            </span>
            <span>·</span>
            <span
              className="tabular-nums"
              data-testid="brand-products-sync-progress-count"
            >
              {t("settings.brand.products.sync.progress.so_far", {
                count: progress.products_seen,
              })}
            </span>
            <span>·</span>
            <span className="tabular-nums">
              {t("settings.brand.products.sync.variant_count", {
                count: progress.variants_seen,
              })}
            </span>
          </>
        ) : (
          <>
            <span>·</span>
            <span>{t("settings.brand.products.sync.progress.waiting")}</span>
          </>
        )}
      </span>

      {/* Outside the aria-hidden wrapper, deliberately — see the class doc.
          `pending` suppresses it: an optimistic submit is a moment, not a
          state, and offering "look again" for it would be noise. */}
      {showSnapshotNotice ? (
        <span
          className="flex w-full flex-wrap items-center gap-2"
          data-testid="brand-products-sync-snapshot"
        >
          <span className="text-muted-foreground text-xs">
            {t("settings.brand.products.sync.progress.snapshot")}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="brand-products-sync-refresh"
          >
            {t("settings.brand.products.sync.progress.refresh")}
          </Button>
        </span>
      ) : null}
    </span>
  )
}
